import { Injectable } from '@angular/core';

import { CategoryService, ProjectService } from '@mirage-projects/cw-commons';
import { Category, EPSG, JobDatatype, PcvizJobAdapter, PcvizJobAdapterFormatDefinition, ResultPage } from '@mirage-projects/cw-models';
import { JobPcvizService } from '@mirage-projects/cw-pcviz-core';
import { FilesHelperService } from '@mirage-projects/cw-utils';

import { WizardFormatDefinition } from '../../../models/wizard-format-option';
import { UploadWizardService } from '../../../services/upload-wizard-service/upload-wizard.service';
import { FileError, PopupNotValidFilesService } from '../popup-not-valid-files/popup-not-valid-files.service';

const FILE_ERROR = {
    EXTENSION_NOT_SUPPORTED: 'EXTENSION_NOT_SUPPORTED',
    CHARACTER_INVALID: 'CHARACTER_INVALID',
    FILE_SIZE_EXCEEDED: 'FILE_SIZE_EXCEEDED',
    TOTAL_SIZE_EXCEEDED: 'TOTAL_SIZE_EXCEEDED',
    FILE_COUNT_EXCEEDED: 'FILE_COUNT_EXCEEDED',
};

@Injectable()
export class CreateWizardInputDataService {
    jobAdapterDetected = false;
    fileErrors: FileError[] = [];
    formatDefinitions: WizardFormatDefinition[] = [];
    uploaderOptions: any[] = [];
    serverCategoryList: Category[] = [];
    config: any;
    readonly LIMIT_TOTAL_SIZE = this.uploadWizardService.isSite() ? Number.MAX_VALUE : 26843545600;

    constructor(
        private uploadWizardService: UploadWizardService,
        private fileHelpersService: FilesHelperService,
        private categoryService: CategoryService,
        private projectService: ProjectService,
        private jobAdapterService: JobPcvizService,
        private popupNotValidFilesService: PopupNotValidFilesService
    ) {
        this.categoryService
            .getCategoryList()
            .subscribe((categories: ResultPage<Category>) => (this.serverCategoryList = categories.results));
    }

    async onFileChange(files: File[]): Promise<void> {
        let currentJobAdapter: PcvizJobAdapter | undefined = this.jobAdapterService.getJobAdapter();
        let possibleJobAdapters: PcvizJobAdapter[] = [];

        if (!this.jobAdapterDetected && !currentJobAdapter?.isSetByUser()) {
            for (const [index, file] of files.entries()) {
                const ext: string = this.fileHelpersService.getExtension(file);
                const uploadersInfo = this.jobAdapterService.getJobAdapterByExt(ext);

                if (uploadersInfo.uploaders.length === 1) {
                    this.jobAdapterDetected = true;
                    this.setJobAdapter(uploadersInfo.uploaders[0]);
                    currentJobAdapter = uploadersInfo.uploaders[0];
                    break;
                }

                if (index === 0) {
                    possibleJobAdapters = uploadersInfo.uploaders;
                } else if (uploadersInfo.uploaders.length && possibleJobAdapters.length) {
                    possibleJobAdapters = possibleJobAdapters.filter((ja: PcvizJobAdapter) => {
                        return uploadersInfo.uploaders.find((uploader: PcvizJobAdapter) => uploader.getId() === ja.getId());
                    });
                }
                if (uploadersInfo.uploaders.length > 0 && uploadersInfo.priority.length === 1) {
                    this.setJobAdapter(uploadersInfo.priority[0]);
                    currentJobAdapter = uploadersInfo.priority[0];
                }
            }

            if (possibleJobAdapters.length === 1) {
                this.setJobAdapter(possibleJobAdapters[0]);
                currentJobAdapter = possibleJobAdapters[0];
            }
        }

        if (!currentJobAdapter) {
            return;
        }

        this.removeNotValidFiles(files, currentJobAdapter);

        const filesType = await this.detect(currentJobAdapter, files);

        if (filesType.length) {
            currentJobAdapter.setCRS(filesType[0].oriCRS);
            this.updatePanelCRS(filesType[0].oriCRS);
            currentJobAdapter.setDestCRS(filesType[0].destCRS);
            currentJobAdapter.setCategory(filesType[0].category);
            currentJobAdapter.setCategoryString(filesType[0].categoryString);

            if (!currentJobAdapter.isSetByUser()) {
                currentJobAdapter.setFormatType(filesType[0].format);
                currentJobAdapter.setIsFinal(filesType[0].isFinal);

                this.setFormat(filesType[0].format);
            }

            if (filesType.length === 1 && filesType[0].isFinal) {
                this.jobAdapterDetected = true;
            }
        }

        if (this.fileErrors.length > 0) {
            this.showNotValidAlert(this.fileErrors);
        }
    }

    acceptedFiles(): string {
        if (this.uploadWizardService.importType === 'project') {
            return this.jobAdapterService
                .createImportZipProjectAdapter()
                .getExtensions()
                .map((extension) => `.${extension}`)
                .join(',');
        }

        if (this.uploadWizardService.isImportStation) {
            return this.jobAdapterService
                .createScanProjectJobAdapter()
                .getExtensions()
                .map((extension) => `.${extension}`)
                .join();
        }

        return this.jobAdapterService.getJobAdapterRegistry().getAcceptedFileExtensions();
    }

    /**
     * Updates the format selection when the file list is modified
     * If there is no files selected and the user hasn't change the selection, clears the format selection
     *
     * @param files List of files added
     */
    refreshFilesList(files: File[]): void {
        if (!files.length) {
            const jobAdapter = this.jobAdapterService.getJobAdapter();

            const shouldClearCurrentJob = !(jobAdapter?.isSetByUser() || this.uploadWizardService.selectInitialJob);

            if (shouldClearCurrentJob) {
                this.setJobAdapter(undefined);
                this.jobAdapterDetected = false;
                this.uploadWizardService.formatSelection = undefined;
            }
        }
    }

    formatChanged(format: PcvizJobAdapterFormatDefinition): void {
        const jobAdapter = this.jobAdapterService.getJobAdapterRegistry().getImporter(format.uploaderName);
        this.setJobAdapter(jobAdapter);

        if (jobAdapter) {
            jobAdapter.setFormatType(format.id);
            jobAdapter.setByUser(true);
            jobAdapter.categoryList = this.serverCategoryList;

            if (this.uploadWizardService.files) {
                this.onFileChange(this.uploadWizardService.files);
            }
        }
    }

    private removeNotValidFiles(files: File[], currentJobAdapter: PcvizJobAdapter): void {
        this.fileErrors = this.checkFileErrors(files, currentJobAdapter);

        const fileNameErrors = this.fileErrors.map((item) => item.name);
        files = files.filter((item: File) => !fileNameErrors.includes(item.name));
        this.uploadWizardService.files = files;
    }

    private checkFileErrors(files: File[], currentJobAdapter: PcvizJobAdapter): FileError[] {
        const errors: FileError[] = [];

        files.forEach((file: File, index: number) => {
            this.checkExtensionNotSupported(file, currentJobAdapter, errors);
            this.checkFileListSize(file, currentJobAdapter, errors);
            this.checkMaximumFileSize(file, currentJobAdapter, errors);
            this.checkMaximumFileCount(file, currentJobAdapter, index, errors);
        });

        return errors;
    }

    private checkExtensionNotSupported(file: File, currentJobAdapter: PcvizJobAdapter, errors: FileError[]): void {
        if (!currentJobAdapter.isFileValid(file)) {
            const errorKey = 'UPLOAD.ERROR_' + FILE_ERROR.EXTENSION_NOT_SUPPORTED;

            this.addFileErrorToList(file, errorKey, errors);
        }
    }

    private checkFileListSize(file: File, currentJobAdapter: PcvizJobAdapter, errors: FileError[]): void {
        let totalSize = 0;
        for (const file of this.uploadWizardService.files) {
            totalSize += this.jobAdapterService.getFileSize(file);
        }

        const maxSize =
            typeof currentJobAdapter.getLimitTotalSize === 'function' && currentJobAdapter.getLimitTotalSize()
                ? currentJobAdapter.getLimitTotalSize()
                : this.LIMIT_TOTAL_SIZE;

        if (totalSize > maxSize) {
            const errorKey = 'UPLOAD.ERROR_' + FILE_ERROR.TOTAL_SIZE_EXCEEDED;

            this.addFileErrorToList(file, errorKey, errors);
        }
    }

    private checkMaximumFileSize(file: File, currentJobAdapter: PcvizJobAdapter, errors: FileError[]): void {
        if (file.size >= currentJobAdapter.getMaximumFileSize()) {
            const errorKey = 'UPLOAD.ERROR_' + FILE_ERROR.FILE_SIZE_EXCEEDED;

            this.addFileErrorToList(file, errorKey, errors);
        }
    }

    private checkMaximumFileCount(file: File, currentJobAdapter: PcvizJobAdapter, fileIndex: number, errors: FileError[]): void {
        const maxFileCountSupported = currentJobAdapter.getMaximumFileCount() ?? Number.MAX_VALUE;

        if (fileIndex >= maxFileCountSupported) {
            const errorKey = 'UPLOAD.ERROR_' + FILE_ERROR.FILE_COUNT_EXCEEDED;

            this.addFileErrorToList(file, errorKey, errors);
        }
    }

    private addFileErrorToList(file: File, errorKey: string, errors: FileError[]): void {
        const notFound = errors.find((item) => item.name === file.name) === undefined;

        if (notFound) {
            errors.push({ errorKey, name: file.name });
        }
    }

    private setFormat(formatId?: string): void {
        this.uploadWizardService.formatSelection = undefined;

        if (formatId) {
            const format = this.uploadWizardService.formatsDefinition.value.find(
                (formatDefinition: any) => formatDefinition.id === formatId
            );
            this.uploadWizardService.formatSelection = format;
        }
    }

    private async detect(currentJobAdapter: PcvizJobAdapter, files: File[]): Promise<JobDatatype[]> {
        if (!files.length) {
            return [];
        }

        currentJobAdapter.setServerCategoryList(this.serverCategoryList);
        const filesType = await currentJobAdapter.detect(files);

        return filesType;
    }

    private setJobAdapter(jobAdapter: PcvizJobAdapter | undefined): void {
        this.jobAdapterService.setJobAdapter(jobAdapter);

        if (!jobAdapter) {
            this.jobAdapterDetected = false;
        }
    }

    private showNotValidAlert(fileErrors: FileError[]): void {
        this.popupNotValidFilesService.openPopupNotValidFiles(fileErrors);
    }

    private updatePanelCRS(value?: string): void {
        if (value) {
            this.projectService.searchEPSG(value.slice(5), 0).subscribe((crs: ResultPage<EPSG>) => {
                if (crs.results.length) {
                    this.uploadWizardService.crsValue = { key: crs.results[0].code, value: crs.results[0].name };
                }
            });
        } else {
            this.uploadWizardService.crsValue = { key: '', value: '' };
        }
    }
}
